import { powerMethodTargetId } from '../AreaOverview/PowerOnboarding';

export const sampleTime = (value) => typeof value === 'number' ? value : Date.parse(value);
export const observationFor = (snapshot, id) => snapshot?.devices?.find((device) => Number(device.deviceId) === Number(id));
export const confirmedTvMethods = (screen, phase) => (screen?.activationStrategy?.powerProfile?.[phase]?.methods || [])
  .filter((method) => method.enabled !== false && method.verification?.status === 'confirmed' && method.type !== 'manual'
    && (powerMethodTargetId(method, screen) === Number(screen.televisionDeviceId) || method.effects?.displayPower === 'yes'));

// Keep a timestamped sleep report even when the subsequent socket disconnects.
// A disconnect on its own is never a sleep report.
export function collectPowerObservations(current, snapshot, startedAt, baseline) {
  const result = { ...(current || {}) };
  (snapshot?.devices || []).forEach((device) => {
    const time = sampleTime(device.collectedAt);
    const previous = observationFor(baseline, device.deviceId);
    if (device.state === 'unknown' && result[device.deviceId]?.state === 'awake') {
      delete result[device.deviceId];
    }
    if (['awake', 'asleep'].includes(device.state) && time >= sampleTime(startedAt)
      && time > (sampleTime(previous?.collectedAt) || 0)
      && time >= (sampleTime(result[device.deviceId]?.collectedAt) || 0)) result[device.deviceId] = device;
  });
  return result;
}

export function discoveredBehaviour(before, after, phase) {
  if (!before || !after || before.state === 'unknown' || after.state === 'unknown') return 'unknown';
  if (phase === 'shutdown') {
    if (before.state !== 'awake') return 'unknown';
    return after.state === 'asleep' ? 'follows' : 'stays-awake';
  }
  if (before.state !== 'asleep') return before.state === 'awake' && after.state === 'awake' ? 'already-awake' : 'unknown';
  return after.state === 'awake' ? 'follows' : 'needs-command';
}

export function discoveryProfile(screen, results) {
  const saved = screen.activationStrategy?.deviceLifecycleProfile || { version: 1, devices: {}, relationships: [] };
  const profile = { ...saved, devices: { ...saved.devices }, relationships: [...(saved.relationships || [])],
    discoveryObservations: { ...saved.discoveryObservations } };
  const playerIds = (screen.players || []).map((player) => Number(player.deviceId || player.id));
  for (const phase of ['shutdown', 'startup']) {
    const run = results[phase];
    if (!run) continue;
    // Retain inconclusive and already-awake observations too, without authorizing a command.
    profile.discoveryObservations[phase] = { methodId: run.method.id, devices: playerIds.map((deviceId) => ({
      deviceId, before: observationFor(run.before, deviceId) || { state: 'unknown' },
      after: run.after[deviceId] || { state: 'unknown' },
    })) };
    const cause = powerMethodTargetId(run.method, screen);
    for (const id of playerIds) {
      const before = observationFor(run.before, id);
      const after = run.after[id];
      const behaviour = discoveredBehaviour(before, after, phase);
      const existing = profile.devices[id] || { deviceId: id, recoveryAnchor: false,
        startup: { desiredState: 'awake', strategy: 'unavailable' }, shutdown: { desiredState: 'standby', strategy: 'unavailable' } };
      // An inconclusive repeat must not erase a working saved configuration.
      if (behaviour === 'unknown' || behaviour === 'already-awake') continue;
      const next = { ...existing, [phase]: { desiredState: phase === 'startup' ? 'awake' : 'standby', strategy: 'unavailable',
        notes: 'Stayed asleep after TV startup; train a separate startup command.' } };
      profile.relationships = profile.relationships.filter((entry) => !(entry.phase === phase && Number(entry.targetDeviceId) === id));
      if (behaviour === 'follows' && cause !== id) {
        next[phase] = { desiredState: phase === 'startup' ? 'awake' : 'standby', strategy: 'follows_device',
          dependsOnDeviceId: cause, trigger: phase === 'startup' ? 'wake' : 'standby', notes: 'Observed during TV power discovery.' };
        profile.relationships.push({ id: `discovery-${phase}-${cause}-${id}`, phase, causeDeviceId: cause, targetDeviceId: id,
          methodId: run.method.id, effect: phase === 'startup' ? 'target_woke' : 'target_slept', status: 'confirmed',
          verifiedAt: new Date(sampleTime(after.collectedAt)).toISOString(), evidence: { commandAccepted: true, operatorObserved: false,
            notes: `Telemetry: ${before.state} at ${before.collectedAt}; ${after.state} at ${after.collectedAt}.` } });
      } else if (behaviour === 'follows' && cause === id) {
        next[phase] = { desiredState: phase === 'startup' ? 'awake' : 'standby', strategy: 'direct_method', methodId: run.method.id,
          notes: 'Direct command observed during TV power discovery.' };
      } else if (behaviour === 'stays-awake' || behaviour === 'already-awake') {
        next[phase] = { desiredState: 'unchanged', strategy: 'always_on', notes: phase === 'startup'
          ? 'Already awake during startup; wake from standby is not tested.' : 'Remained awake during the shutdown observation window.' };
      }
      // Retain the recovery requirement, and save conflicting observed relationships so execution blocks it.
      if (existing.recoveryAnchor) next.shutdown = { ...existing.shutdown, desiredState: 'remain_awake', strategy: 'always_on' };
      profile.devices[id] = next;
    }
  }
  return profile;
}
